import { useEffect, useRef } from 'react';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import { overlay } from 'overlay-kit';
import { v4 as uuidv4 } from 'uuid';
import './events.quiz-share';
import '@/shared/tracking/events.quiz-share-guest';

// enter_space·enter_play 는 이번 범위 밖의 기존 raw 이벤트라 그대로 둔다.
import { logger } from '@zep/core-logger';
import { trackEvent } from '@zep/logger/tracking';
import { useLocale, useViewport } from '@/hooks';
import { useUser } from '@/lib/auth';
import { useCanCreateClassroom } from '@/shared/hooks';
import { useCopyQuiz } from '@/shared/quizCopy/hooks/useCopyQuiz';
import { Button, DialogBody, DialogHeader, ResponsiveDialog } from '@/ui_v3';
import { cn } from '@/utils/tailwind.util';
import { GuestCopyGuideOverlay } from './GuestCopyGuideOverlay';
import { LinkContentToClassroomSection } from './LinkContentToClassroomSection';
import { ShareContent } from './ShareContent';

// 실제 공유값이 DOM에 남지 않도록 렌더하는 더미 값.
const OBSCURED_ENTER_CODE = 123456;
const OBSCURED_SHARE_URL = `${process.env.NEXT_PUBLIC_BASE_URL}/play/xxxxxxxx`;
interface ContentShareModalCommonProps {
  title: string;
  /**
   * 공유 수단 이벤트(`quiz_share_method_click`)의 `place`. 같은 모달을 여러 화면이
   * 쓰므로 출처를 구분한다. 생략하면 `trackLocation` 값을 그대로 쓴다.
   */
  trackPlace?: string;
  /** 학급 맥락에서 열렸다면 그 학급. 진입·공유 수단 이벤트를 학급으로 잇는다. */
  classroomId?: string;
  contentId: string;
  contentOwnerId: string;
  shareUrl: string;
  hideFooter?: boolean;
  /**
   * 클래스 상세 페이지처럼 이미 특정 클래스 컨텍스트에 있는 곳에서 열린 공유 모달은
   * 클래스 연결 UI 를 노출하지 않는다.
   */
  hideClassroomLinking?: boolean;
  onClose: () => void;
}
interface QuizContentShareModalProps extends ContentShareModalCommonProps {
  contentType: 'QUIZSET';
  enterCode: number;
  spaceHashId: string;
  /**
   * 비소유자에게 공유 정보 대신 복사를 안내한다(퀴즈룸 전용 opt-in).
   *
   * 소유 여부만으로 자동 분기하지 않는다 — 이 모달은 퀴즈 상세·보드·학급·내 콘텐츠 등
   * 11곳이 공유하고, 그중 "남의 콘텐츠를 공유"하는 것이 정상인 화면이 있다. 켜는 곳은
   * 플레이 헤더·사이드바뿐이다. 퀴즈셋 전용이라는 것은 타입으로 못박는다.
   */
  guestCopyGuide?: boolean;
}
interface BoardContentShareModalProps extends ContentShareModalCommonProps {
  contentType: 'BOARD';
  enterCode: number;
  spaceHashId?: never;
}
export type ContentShareModalProps = QuizContentShareModalProps | BoardContentShareModalProps;

/** 진입 이벤트(`enter_share`)에 얹을 문맥. 아는 화면만 채운다. */
type ShareTrackingContext = {
  trackLocation: string;
  isOwner?: boolean;
};
type ShowShareModalPayload = (Omit<QuizContentShareModalProps, 'onClose'> & ShareTrackingContext) | (Omit<BoardContentShareModalProps, 'onClose'> & ShareTrackingContext);

/**
 * 소유자가 **아님이 확정된** 상태.
 *
 * `isOwner` 는 계측용이라 3-상태(내 것 / 남의 것 / 판정 불가)인데 게이팅은 2-상태다.
 * 그 간극이 이 함수의 존재 이유다 — "확정"이 아닌 두 경우를 명시적으로 배제한다.
 *
 * - 인증 미초기화: 소유자도 잠깐 비로그인처럼 보여 안내가 번쩍인다.
 * - `contentOwnerId` 빈 값(학급 공유 경로): 로그인 사용자는 `isOwner === false` 에서
 *   이미 걸리지만 **게스트는 `!isAuthenticated` 로 통과해 버린다.** 이 절이 막는 건
 *   그 조합이다.
 */
const isConfirmedNonOwner = (auth: {
  isAuthenticated: boolean;
  isInitialized: boolean;
}, contentOwnerId: string, isOwner: boolean | undefined) => auth.isInitialized && !!contentOwnerId && (!auth.isAuthenticated || isOwner === false);

/**
 * 안내가 어느 진입점에서 열렸는지. `enter_share` 의 `place` 어휘를 그대로 쓴다.
 *
 * 켜는 곳이 헤더·사이드바 둘뿐이라 나머지는 헤더로 접는다 — 값이 늘면 이벤트가 아니라
 * 이 함수를 고친다.
 */
const resolveGuestGuidePlace = (trackPlace: string | undefined) => trackPlace === 'play_sidebar' ? 'play_sidebar' : 'play_header';

/**
 * 안내 모달의 노출·종료를 한 시도로 묶는다.
 *
 * 컴포넌트 본문에 두면 분기가 늘어 복잡도가 오른다. 계측 래퍼를 새로 만드는 게 아니라
 * 이 모달 전용 effect 를 옮겨 둔 것이라 파일 안에 둔다.
 */
const useGuestGuideTracking = ({
  enabled,
  place,
  quizSetId
}: {
  enabled: boolean;
  place: 'play_header' | 'play_sidebar';
  quizSetId: string;
}) => {
  // 이 모달 한 번이 곧 시도 하나다. 열릴 때 만들어 저장까지 들고 간다.
  const shareFlowIdRef = useRef<string>();
  if (enabled && !shareFlowIdRef.current) {
    shareFlowIdRef.current = uuidv4();
  }
  // 닫힘은 언마운트로도 일어난다. CTA 를 눌렀는지 기억해 두 갈래를 가른다.
  const closedByCopyCtaRef = useRef(false);
  const shareFlowId = shareFlowIdRef.current ?? '';
  useEffect(() => {
    if (!enabled || !shareFlowId) return;
    trackEvent('quiz_share_guest_guide_view', {
      share_flow_id: shareFlowId,
      place,
      quiz_set_id: quizSetId
    });
    return () => {
      trackEvent('quiz_share_guest_guide_close', {
        share_flow_id: shareFlowId,
        place,
        quiz_set_id: quizSetId,
        close_reason: closedByCopyCtaRef.current ? 'copy_cta' : 'dismiss'
      });
    };
  }, [enabled, shareFlowId, place, quizSetId]);
  return {
    shareFlowId,
    markClosedByCopyCta: () => {
      closedByCopyCtaRef.current = true;
    }
  };
};
export function ContentShareModal(props: ContentShareModalProps) {
  const {
    locale
  } = useLocale();
  const {
    isMobile
  } = useViewport();
  const {
    t
  } = useTranslation(['common']);
  // 학생/로그아웃 사용자는 학급 목록도 만들기 버튼도 렌더되지 않는다. 그때도
  // 이 값이 true 면 모달이 커지고 오른쪽에 빈 198px 패널만 남으며 퀴즈 진입
  // 버튼까지 사라진다. 섹션 내부 노출 조건(useCanCreateClassroom)과 맞춘다.
  const canCreateClassroom = useCanCreateClassroom();
  // 제작자와 학생·타인이 같은 모달을 쓴다. 소유 여부 없이는 공유 수단별 선생님
  // 행동을 가를 수 없다. 판정은 레포 선례와 같은 축(quizUserId ↔ ownerId)을 쓴다.
  const {
    user,
    isAuthenticated,
    isInitialized
  } = useUser();
  // 학급 공유는 contentOwnerId 가 없을 때 빈 문자열을 넘긴다
  // (classroomContentActions.ts:53,70). 한쪽이라도 비면 비교 결과가 false 가 되어
  // 판정 불가인 공유가 전부 "남의 콘텐츠"로 집계된다.
  const isOwner = user?.quizUserId && props.contentOwnerId ? props.contentOwnerId === user.quizUserId : undefined;
  const showGuestCopyGuide = props.contentType === 'QUIZSET' && !!props.guestCopyGuide && isConfirmedNonOwner({
    isAuthenticated,
    isInitialized
  }, props.contentOwnerId, isOwner);
  const {
    copyQuiz
  } = useCopyQuiz();
  const guestGuidePlace = resolveGuestGuidePlace(props.trackPlace);

  // 퍼널의 첫 칸. 모달은 열릴 때 이미 화면에 있으므로 노출 = 이 분기가 켜진 시점이다.
  // 분모는 `quiz_play_init` 의 `is_owner:false` 라 여기서 다시 세지 않는다.
  const {
    shareFlowId,
    markClosedByCopyCta
  } = useGuestGuideTracking({
    enabled: showGuestCopyGuide,
    place: guestGuidePlace,
    quizSetId: props.contentId
  });
  const canShowLinkContentToClassroom = !showGuestCopyGuide && !props.hideClassroomLinking && canCreateClassroom;
  return <ResponsiveDialog size={isMobile || canShowLinkContentToClassroom ? 'lg' : 'md'} onClose={props.onClose} className={canShowLinkContentToClassroom ? 'mobile-lg:!h-[583px]' : undefined} data-sentry-element="ResponsiveDialog" data-sentry-component="ContentShareModal" data-sentry-source-file="ContentShareModal.tsx">
      <DialogHeader title={t('shareModal.title')} hasCloseButton={true} onClose={props.onClose} data-sentry-element="DialogHeader" data-sentry-source-file="ContentShareModal.tsx" />
      <DialogBody className={cn('flex min-h-0 flex-col items-center gap-md overflow-auto px-md mobile-lg:px-lg', canShowLinkContentToClassroom && 'overflow-hidden mobile-lg:min-h-0 mobile-lg:flex-1 mobile-lg:flex-row mobile-lg:items-stretch mobile-lg:gap-lg')} data-sentry-element="DialogBody" data-sentry-source-file="ContentShareModal.tsx">
        <div className={cn('relative z-0 flex w-full min-w-0 flex-col items-center gap-md pb-lg', canShowLinkContentToClassroom && 'min-h-0 flex-1 pb-0 mobile-lg:w-[450px] mobile-lg:flex-none mobile-lg:shrink-0 mobile-lg:pb-lg')}>
          <div className="relative flex w-full min-w-0 flex-1 flex-col">
            <ShareContent title={props.title} enterCode={showGuestCopyGuide ? OBSCURED_ENTER_CODE : props.enterCode} shareUrl={showGuestCopyGuide ? OBSCURED_SHARE_URL : props.shareUrl} place={props.trackPlace ?? 'unknown'} contentType={props.contentType} contentId={props.contentId} classroomId={props.classroomId} isOwner={isOwner} obscured={showGuestCopyGuide} data-sentry-element="ShareContent" data-sentry-source-file="ContentShareModal.tsx" />
            {showGuestCopyGuide && <GuestCopyGuideOverlay onCopy={() => {
            markClosedByCopyCta();
            trackEvent('quiz_share_guest_copy_click', {
              share_flow_id: shareFlowId,
              place: guestGuidePlace,
              quiz_set_id: props.contentId
            });
            copyQuiz(props.contentId, 'guest_share_copy', {
              copySource: 'guest_share',
              shareFlowId
            });
            props.onClose();
          }} />}
          </div>
          {!showGuestCopyGuide && props.contentType === 'QUIZSET' && !props.hideFooter && !canShowLinkContentToClassroom && <Link href={props.shareUrl} target="_blank" className="w-full">
                <Button onClick={() => {
            logger.track('enter_space', {
              locale
            });
            logger.track('enter_play', {
              location: 'shareModal',
              spaceId: props.spaceHashId,
              isAuthor: true
            });
          }} className="w-full">
                  {t('global.enter.button')}
                </Button>
              </Link>}
        </div>
        {canShowLinkContentToClassroom && <LinkContentToClassroomSection contentId={props.contentId} contentType={props.contentType} />}
      </DialogBody>
    </ResponsiveDialog>;
}
export const showShareModal = (payload: ShowShareModalPayload) => {
  // 공유 모달 진입은 이 이벤트 하나로만 센다. 같은 행동을 새 이름으로 또 쌓으면
  // 집계 기준이 갈린다 — 진입점·소유 여부·학급은 prop 으로 얹는다.
  trackEvent('enter_share', {
    location: payload.trackLocation,
    contentId: payload.contentId,
    contentType: payload.contentType,
    quizSetId: payload.contentId,
    ...(payload.trackPlace && {
      place: payload.trackPlace
    }),
    ...(payload.isOwner !== undefined && {
      is_owner: payload.isOwner
    }),
    ...(payload.classroomId && {
      classroom_id: payload.classroomId
    })
  });
  // 호출부가 place 를 따로 주지 않으면 기존 trackLocation 어휘를 그대로 쓴다.
  if (payload.contentType === 'QUIZSET') {
    const {
      trackLocation,
      ...modalProps
    } = payload;
    overlay.open(({
      unmount
    }) => <ContentShareModal {...modalProps} trackPlace={modalProps.trackPlace ?? trackLocation} onClose={unmount} />);
    return;
  }
  const {
    trackLocation,
    ...modalProps
  } = payload;
  overlay.open(({
    unmount
  }) => <ContentShareModal {...modalProps} trackPlace={modalProps.trackPlace ?? trackLocation} onClose={unmount} />);
};